import clsx from 'clsx'
import { Filter, X } from 'lucide-react'
import FigureValue from '@/components/dashboards/FigureValue'
import {
  DIMENSION_LABEL,
  NO_BROKERAGE_LABEL,
  OUTCOME_LABEL,
  cleanFilters,
  encodeFilters,
  measureFigure,
  toggleFilterValue,
  totalsOf,
  type AnalyticsDimension,
  type AnalyticsFilters,
  type Outcome,
} from '@/services/dashboardAnalytics'
import { usePivot } from './analyticsQueries'

export interface CohortPreset {
  id: string
  label: string
  filters: AnalyticsFilters
  hint: string
}

/** T-52: whole pipeline vs funded only vs a chosen cohort. Presets are
 *  outcome sets; anything else is added by clicking a bar, a row or a chip. */
export const COHORT_PRESETS: CohortPreset[] = [
  { id: 'all', label: 'Whole pipeline', filters: {}, hint: 'Every live, non-archived deal' },
  {
    id: 'active',
    label: 'Open pipeline',
    filters: { outcome: ['requested', 'signed'] },
    hint: 'Requested and signed — not funded, not dead',
  },
  {
    id: 'signed',
    label: 'Signed + funded',
    filters: { outcome: ['signed', 'funded'] },
    // Not the board's "Total exposure" set, which starts at IC approval: this
    // is the "signed" outcome (term sheet signed onward) plus funded.
    hint: 'Term sheet signed, IC approved, rate locked or funded. The headline Total exposure starts at IC approval, so the two counts differ.',
  },
  { id: 'funded', label: 'Funded only', filters: { outcome: ['funded'] }, hint: 'Stage Funded' },
]

export function presetFor(filters: AnalyticsFilters): CohortPreset | null {
  const key = encodeFilters(filters)
  return COHORT_PRESETS.find((preset) => encodeFilters(preset.filters) === key) ?? null
}

function valueLabel(dimension: string, value: string | null): string {
  if (value == null) return dimension === 'brokerage' ? NO_BROKERAGE_LABEL : dimension === 'responsibleParty' ? 'Unassigned' : 'Not recorded'
  if (dimension === 'outcome' && value in OUTCOME_LABEL) return OUTCOME_LABEL[value as Outcome]
  return value
}

export function dimensionLabel(dimension: string): string {
  return DIMENSION_LABEL[dimension as AnalyticsDimension] ?? dimension
}

interface CohortBarProps {
  cohort: AnalyticsFilters
  onChange: (next: AnalyticsFilters) => void
  className?: string
  /** Hide the population read-out (when the host prints its own). */
  hidePopulation?: boolean
}

/**
 * The board's cohort, stated and editable in one line: preset segments, the
 * active filter chips as a breadcrumb (× removes one), and the population the
 * cohort selects — counted by the server, every number clickable.
 */
export default function CohortBar({ cohort, onChange, className, hidePopulation = false }: CohortBarProps) {
  const clean = cleanFilters(cohort)
  const preset = presetFor(clean)
  const chips = Object.entries(clean).flatMap(([dimension, values]) =>
    (values ?? []).map((value) => ({ dimension, value })),
  )
  const population = usePivot(hidePopulation ? null : { dimension: 'outcome', filters: clean })
  const totals = totalsOf(population.data)

  return (
    <div
      className={clsx(
        'flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-amber-400/[0.09] bg-gradient-to-r from-[#0d0c0a] to-[#090909] px-2 py-1',
        className,
      )}
    >
      <span className="flex items-center gap-1 font-mono text-[8.5px] uppercase tracking-[0.2em] text-amber-200/60">
        <Filter size={9} aria-hidden="true" /> Cohort
      </span>
      <div role="radiogroup" aria-label="Cohort preset" className="flex items-center gap-0.5 rounded border border-amber-400/10 p-0.5">
        {COHORT_PRESETS.map((entry) => {
          const active = preset?.id === entry.id
          return (
            <button
              key={entry.id}
              type="button"
              role="radio"
              aria-checked={active}
              title={entry.hint}
              onClick={() => onChange(entry.filters)}
              className={clsx(
                'rounded px-2 py-[3px] text-[10px] transition-colors',
                active ? 'bg-amber-400/[0.12] text-amber-100' : 'text-stone-500 hover:text-stone-200',
              )}
            >
              {entry.label}
            </button>
          )
        })}
      </div>

      {chips.length > 0 && !preset && (
        <nav aria-label="Active filters" className="flex min-w-0 flex-wrap items-center gap-1">
          {chips.map(({ dimension, value }) => (
            <span
              key={`${dimension}:${value ?? '∅'}`}
              className="inline-flex max-w-[260px] items-center gap-1 rounded-full border border-amber-400/25 bg-amber-400/[0.06] py-[2px] pl-2 pr-1 text-[10px] text-amber-100"
            >
              <span className="text-stone-500">{dimensionLabel(dimension)}:</span>
              <span className="truncate" title={valueLabel(dimension, value)}>
                {valueLabel(dimension, value)}
              </span>
              <button
                type="button"
                onClick={() => onChange(toggleFilterValue(clean, dimension, value))}
                aria-label={`Remove ${dimensionLabel(dimension)} ${valueLabel(dimension, value)}`}
                className="rounded-full p-0.5 text-stone-500 hover:bg-amber-400/15 hover:text-amber-100"
              >
                <X size={9} />
              </button>
            </span>
          ))}
          {Object.keys(clean).length > 1 && (
            <button
              type="button"
              onClick={() => onChange({})}
              className="text-[10px] text-stone-500 underline-offset-2 hover:text-stone-200 hover:underline"
            >
              Clear all
            </button>
          )}
        </nav>
      )}

      {!hidePopulation && (
        <div className="ml-auto flex items-center gap-2 font-mono text-[10px] tabular-nums text-stone-400">
          {population.isLoading ? (
            <span className="text-stone-600">Counting…</span>
          ) : population.error ? (
            <span className="text-red-300/80">Population unavailable</span>
          ) : totals ? (
            <>
              <FigureValue figure={measureFigure(totals, 'count', { title: 'Cohort deals', label: 'Deals' })} />
              <span className="text-stone-600">deals ·</span>
              <FigureValue figure={measureFigure(totals, 'commitment', { title: 'Cohort deals' })} />
              <span className="text-stone-600">commitment</span>
            </>
          ) : null}
        </div>
      )}
    </div>
  )
}
